import React, { useEffect, useState } from 'react';
import axios from '../../api/axios';
import { 
  Badge, 
  Button, 
  Card, 
  Col, 
  Form, 
  Modal, 
  Pagination, 
  Row, 
  Spinner, 
  Table,
  InputGroup,
  FormControl,
  FormSelect
} from 'react-bootstrap';
import DatePicker from "react-datepicker";
import { Search, Printer, FileExcel, FileEarmarkExcel, ArrowLeft, FileEarmarkSpreadsheet } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import Header from '../../layouts/Header';
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from 'react-router-dom';

const CustomerReports = () => {
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);
  const accessToken = localStorage.getItem('authToken');
  const navigate = useNavigate(); // Add this line
  
  // State variables
  const [customerData, setCustomerData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState('Recent Users');
  const [levels, setLevels] = useState([]);
  const [levelId, setLevelId] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [error, setError] = useState('');
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState('');
  const [selectedOutletName, setSelectedOutletName] = useState('');
  const [viewDetailsModal, setViewDetailsModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);
  const [orderHistoryData, setOrderHistoryData] = useState([]);

  // Fetch initial data
  useEffect(() => {
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    const start = new Date(firstOfMonth.setHours(23, 59, 59, 999));
    const end = new Date(currentDate.setHours(23, 59, 59, 999));
    
    setStartDate(start);
    setEndDate(end);
    fetchLevelsData();
    getOutlets();
  }, []);

  // API functions
  const getOutlets = async () => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axios.get("outlet", { headers });
      if (response?.data?.status === true) {
        const activeOutlets = response?.data?.data?.outlets.filter(outlet => outlet?.status === "ACTIVE" || "INACTIVE");
        setOutlets(activeOutlets);
      }
    } catch (error) {
      handleApiError(error, "Failed to fetch outlets");
    }
  };

  const fetchLevelsData = async () => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axios.get("level", { headers });
      if (response?.data?.status === true) {
        setLevels(response?.data?.data?.levels);
      }
    } catch (error) {
      handleApiError(error, "Failed to fetch levels");
    }
  };

  const handleDownloadExcel = async () => {
    setDownloadBtnLoading(true);
    const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
    const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));
    getCustomerData(selectedAccount, currentPage, searchText, levelId, sortOrder, recordsPerPage, adjustedStartDate, adjustedEndDate, selectedOutlet, true);
  };

  const getCustomerData = async(account, page, search, level, sort, limit, start, end, outlet, download) => {
    setLoading(true);
    try {
      const headers = { 
        Authorization: `Bearer ${accessToken}`,
        "content-type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
      };
      let url = `user/getUsersWithOrders`;
      const isRecentUsersSelected = account === "Recent Users";

      // Build query parameters
      const params = new URLSearchParams();
      if (!search) {
        if (isRecentUsersSelected) {
          params.append('fromDate', start?.toISOString().split("T")[0]);
          params.append('toDate', end?.toISOString().split("T")[0]);
        }
      } else {
        params.append('search', search);
      }

      if (account !== "" && account !== "Recent Users") {
        params.append('filter', account);
      }

      if (level !== "") {
        params.append('levelId', level);
      }

      if (sort !== "") {
        params.append('sortOrder', sort);
      }

      if (outlet) {
        params.append('outletId', outlet);
      }

      if (download) {
        params.append('isDownload', download);
      }

      params.append('page', page);
      params.append('limit', limit);

      const response = await axios.get(`${url}?${params.toString()}`, { 
        headers,
        responseType: download ? 'blob' : 'json'
      });

      console.log("Response Response :", response);

      if (download) {
        const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const url = window.URL.createObjectURL(blob);
        console.log("Download Url Log :", url);
        
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${selectedOutletName} Customer Report (${start?.toISOString().split("T")[0]} - ${end?.toISOString().split("T")[0]}).xlsx`);
        document.body.appendChild(link);
        link.click();

        // Clean up
        link.remove();
        window.URL.revokeObjectURL(url);

        toast.success('Excel file downloaded successfully!', {
          position: "top-right",
          autoClose: 3000,
          theme: "light"
        });
      } else {
          setCustomerData(response?.data?.data?.usersWithOrders || []);
          setFilteredData(response?.data?.data?.usersWithOrders || []);
          setTotalPages(response?.data?.data?.pagination?.total_pages || 1);
          toast.success(response?.data?.message, {
            position: "top-right",
            autoClose: 3000,
            theme: "colored"
          });
      }
      
    } catch (error) {
      handleApiError(error, "Failed to fetch customer data");
      setFilteredData([]);
    } finally {
      setLoading(false);
      setDownloadBtnLoading(false);
    }
  }

  const handleApiError = (error, defaultMessage) => {
    console.log("Eroror :", error);
    
    let errorMessage = defaultMessage;
    if (error?.response) {
      if (error.response?.status === 406) {
        errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error;
      } else if (error.response?.status === 400) {
        errorMessage = error?.response?.data?.error;
      } else if (error.response?.status === 412 || error.response?.status === 404) {
        errorMessage = error?.response?.data?.data?.message;
      } else if (error.response?.status === 500) {
        errorMessage = error?.response?.data?.error;
      }
    }
    toast.error(errorMessage, {
      position: "top-right",
      autoClose: 3000,
      theme: "light",
    });
  };

  // Event handlers
  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
      const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
      const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));
      getCustomerData(selectedAccount, page, searchText, levelId, sortOrder, recordsPerPage, adjustedStartDate, adjustedEndDate, selectedOutlet);
    }
  };

  const handleRecordsPerPageChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setRecordsPerPage(value);
    setCurrentPage(1); // Reset to first page when changing records per page
    const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
    const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));
    getCustomerData(selectedAccount, 1, searchText, levelId, sortOrder, value, adjustedStartDate, adjustedEndDate, selectedOutlet);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCurrentPage(1);
    const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
    const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));
    await getCustomerData(selectedAccount, 1, searchText, levelId, sortOrder, recordsPerPage, adjustedStartDate, adjustedEndDate, selectedOutlet);
  };

  const handleEndDateChange = (date) => {

    const start = new Date(startDate);
    const end = new Date(date);

    // Remove time part
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const maxEndDate = new Date(start);
    maxEndDate.setDate(start.getDate() + 60);

    if (maxEndDate && startDate && maxEndDate < startDate) {
      setError('End date cannot be earlier than start date');
    } else {
      setError('');
      setEndDate(date);
    }
  };

  const viewOrderDetails = async(data) => {
    await getOrderHistory(data?.mobile, data?._id);
    setViewDetailsModal(true);
    console.log("Data :", data)
    setSelectedCustomer(data);
  };

  const getOrderHistory = async (mobile, userId) => {
    try {
      const headers = {
        Authorization: `Bearer ${accessToken}`
      }
      
      // const response = await axios.get(`order/getOrdersForAdmin?search=${mobile}`, { headers });
      const response = await axios.get(`user/getUserOrders?userId=${userId}`, { headers });

      console.log("Response of Order Report of Customer :", response?.data?.data);

      let orderHistoryData = response?.data?.data;

      const sortedData = [...orderHistoryData].sort(
        (a, b) => new Date(b.orderDetails.createdAt) - new Date(a.orderDetails.createdAt)
      );

      setOrderHistoryData(sortedData);
    } catch (error) {
      console.log("Error In Getting Order History :", error);
    }
  }

  const closeModal = () => {
    setViewDetailsModal(false);
    setSelectedCustomer(null);
  };

  // Report generation functions
  const generateReport = (includeId = false) => {
    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    };

    const tableContent = `
      <table style="border-collapse: collapse; width: 100%; font-size: 12px;">
        <thead>
          <tr style="background-color: #f8f9fa;">
            <th style="border: 1px solid #dee2e6; padding: 8px;">#</th>
            <th style="border: 1px solid #dee2e6; padding: 8px;">Joined On</th>
            <th style="border: 1px solid #dee2e6; padding: 8px;">Customer Name</th>
            ${includeId ? '<th style="border: 1px solid #dee2e6; padding: 8px;">ID</th>' : ''}
            <th style="border: 1px solid #dee2e6; padding: 8px;">Email</th>
            <th style="border: 1px solid #dee2e6; padding: 8px;">Mobile</th>
            <th style="border: 1px solid #dee2e6; padding: 8px;">Outlet</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">Orders</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">Cancelled</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">Visits</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">Spendings (₹)</th>
          </tr>
        </thead>
        <tbody>
          ${filteredData?.length > 0 ? 
            filteredData.map((customer, index) => `
              <tr>
                <td style="border: 1px solid #dee2e6; padding: 8px;">${(currentPage - 1) * recordsPerPage + index + 1}</td>
                <td style="border: 1px solid #dee2e6; padding: 8px;">${customer?.createdAt}</td>
                <td style="border: 1px solid #dee2e6; padding: 8px;">${customer?.name || '-'}</td>
                ${includeId ? `<td style="border: 1px solid #dee2e6; padding: 8px; font-family: monospace;">${customer?._id || '-'}</td>` : ''}
                <td style="border: 1px solid #dee2e6; padding: 8px;">${customer?.email || '-'}</td>
                <td style="border: 1px solid #dee2e6; padding: 8px;">${customer?.mobile || '-'}</td>
                <td style="border: 1px solid #dee2e6; padding: 8px;">${customer?.firstOrder?.outletName || '-'}</td>
                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">${customer?.orderCount || 0}</td>
                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">${customer?.cancelledOrderCount || 0}</td>
                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">${customer?.numberOfVisits || 0}</td>
                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">${customer?.totalGrandTotal?.toFixed(2) || '0.00'}</td>
              </tr>
            `).join("") :
            `<tr>
              <td colspan="${includeId ? 11 : 10}" style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">No customers found</td>
            </tr>`
          }
        </tbody>
      </table>
    `;

    const win = window.open("", "_blank");
    win.document.write(`
      <html>
        <head>
          <title>Customer Report ${includeId ? 'with IDs' : ''}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
            .title { font-size: 18px; font-weight: bold; }
            .date-range { font-size: 14px; color: #666; }
            table { width: 100%; border-collapse: collapse; }
            th { background-color: #f8f9fa; text-align: left; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">Customer Report ${includeId ? 'with IDs' : ''}</div>
            <div class="date-range">Page ${currentPage} of ${totalPages}</div>
          </div>
          ${tableContent}
          <script>
            setTimeout(() => {
              window.print();
              window.close();
            }, 500);
          </script>
        </body>
      </html>
    `);
    win.document.close();
  };

  // Pagination rendering
  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage, endPage;

    if (totalPages <= maxVisiblePages) {
      startPage = 1;
      endPage = totalPages;
    } else {
      const maxVisibleBeforeCurrent = Math.floor(maxVisiblePages / 2);
      const maxVisibleAfterCurrent = Math.ceil(maxVisiblePages / 2) - 1;
      
      if (currentPage <= maxVisibleBeforeCurrent) {
        startPage = 1;
        endPage = maxVisiblePages;
      } else if (currentPage + maxVisibleAfterCurrent >= totalPages) {
        startPage = totalPages - maxVisiblePages + 1;
        endPage = totalPages;
      } else {
        startPage = currentPage - maxVisibleBeforeCurrent;
        endPage = currentPage + maxVisibleAfterCurrent;
      }
    }

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis-start" />);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item 
          key={i} 
          active={i === currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis-end" />);
      }
      items.push(
        <Pagination.Item 
          key={totalPages} 
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  const [openOrderDetailModal, setOpenOrderDetailModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState({});

  const handleOpenOrderDetailModal = async(order) => {
    console.log("Single Order Details :", order);
    await getSingleOrderDetais(order?.orderDetails?.orderId);
    setOpenOrderDetailModal(true);
  };

  const getSingleOrderDetais = async(orderId) => {
    try {
      const headers = {
        Authorization: `Bearer ${accessToken}`
      }
      const response = await axios.get(`order/getOrdersForAdmin?_id=${orderId}`, { headers });
      console.log("Response of Single Order Details :", response?.data?.data);
      setSelectedOrder(response?.data?.data?.totalOrdersData);
    } catch (error) {
      console.log("Error in order/getOrdersForAdmin :", error);
    }
  }

  const calculateTotal = () => {
    let total = 0;

    selectedOrder?.orderItems?.forEach((item) => {
      let itemPrice = selectedOrder?.order?.coupon?.isMrpDiscount
        ? item.mrpRate
        : item.rate;
      let addonsTotal = item?.addons?.reduce(
        (sum, addon) => sum + addon?.price,
        0
      );
      total += (itemPrice + addonsTotal) * item?.quantity;
    });

    total += selectedOrder?.order?.platformFees || 0;
    total += selectedOrder?.order?.packingCharges || 0;
    total += selectedOrder?.order?.deliveryCharges || 0;

    return total.toFixed(2);
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className={`main main-app p-3 p-lg-4 ${skin === 'dark' ? 'bg-dark text-light' : 'bg-light'}`}>
        <Card className={`mb-4 ${skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}`}>
          {/* <Card.Header className={`d-flex justify-content-between align-items-center ${skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}`}>
            <h5 className="mb-0">
              <i className="bi bi-people-fill me-2"></i>Customer Reports
            </h5>
          </Card.Header> */}

          <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
              <div>
                  <h4 className="mb-0 fw-bold d-inline-block">
                      <i className="bi bi-people-fill me-2"></i>CUSTOMER REPORT
                  </h4>
              </div>
              <div>
                  <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      onClick={() => navigate('/reports/all-reports')}
                      className="me-3 d-flex align-items-center fw-semibold"
                      style={{ height: '38px' }}
                  >
                      <ArrowLeft className="me-1" /> Back to Reports
                  </Button>
              </div>
          </div>
          <hr className='mt-0 mb-0' />
          
          <Card.Body>
            {/* Filters Section */}
            <div className="mb-4 p-3 bg-light rounded-3">
              <Form onSubmit={handleSubmit}>
                <Row className="g-3">
                  <Col md={3}>
                    <Form.Group controlId="outletSelect">
                      <Form.Label className="fw-semibold">
                        <i className="bi bi-shop me-2"></i>Outlet
                      </Form.Label>
                      <Form.Select
                        value={selectedOutlet}
                        onChange={(e) => 
                          {
                            setSelectedOutlet(e.target.value);
                            setSelectedOutletName(e.target.selectedOptions[0]?.label);
                          }
                        }
                        className={skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}
                      >
                        <option value="">All Outlets</option>
                        {outlets.map((outlet) => (
                          <option key={outlet._id} value={outlet._id} label={`${outlet?.name} (${outlet?.city})`} >
                            {outlet.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group controlId="startDate">
                      <Form.Label className="fw-semibold">
                        <i className="bi bi-calendar me-2"></i>Start Date
                      </Form.Label>
                      <DatePicker
                        selected={startDate}
                        onChange={(date) => {
                          setStartDate(date);
                          // Reset end date if it goes beyond new range
                          if (endDate && date && endDate > new Date(date.getTime() + 60 * 24 * 60 * 60 * 1000)) {
                            setEndDate(null);
                          }
                        }}
                        dateFormat="dd-MM-yyyy"
                        placeholderText="Start Date"
                        className={`form-control ${skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}`}
                        disabled={levelId !== "" || selectedAccount !== "Recent Users"}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group controlId="endDate">
                      <Form.Label className="fw-semibold">
                        <i className="bi bi-calendar me-2"></i>End Date
                      </Form.Label>
                      <DatePicker
                        selected={endDate}
                        onChange={handleEndDateChange}
                        dateFormat="dd-MM-yyyy"
                        placeholderText="End Date"
                        className={`form-control ${skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}`}
                        minDate={startDate}
                        maxDate={startDate ? new Date(startDate.getTime() + 60 * 24 * 60 * 60 * 1000) : null}
                        disabled={levelId !== "" || selectedAccount !== "Recent Users"}
                      />
                      {error && <div className="text-danger small mt-1">{error}</div>}
                    </Form.Group>
                  </Col>


                  <Col md={2}>
                    <Form.Group controlId="levelSelect">
                      <Form.Label className="fw-semibold">
                        <i className="bi bi-star me-2"></i>Level
                      </Form.Label>
                      <Form.Select 
                        onChange={(e) => setLevelId(e.target.value)} 
                        value={levelId}
                        className={skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}
                      >
                        <option value="">All Levels</option>
                        {levels.map((level) => (
                          <option key={level._id} value={level._id}>{level.title}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group controlId="accountSelect">
                      <Form.Label className="fw-semibold">
                        <i className="bi bi-funnel me-2"></i>Account Type
                      </Form.Label>
                      <Form.Select 
                        value={selectedAccount} 
                        onChange={(e) => setSelectedAccount(e.target.value)}
                        className={skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}
                      >
                        <option value="Recent Users">Recent Users</option>
                        <option value="DELETED">Deleted Accounts</option>
                        <option value="ONE-TIME">One Timers</option>
                        <option value="LOYAL">Loyal Customers</option>
                        <option value="JOINING_COUPON_USED">First 59 Used</option>
                        <option value="NO_ORDER_USER">Not Ordered</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="g-3 mt-1">
                  <Col md={3}>
                    <Form.Group controlId="sortSelect">
                      <Form.Label className="fw-semibold">
                        <i className="bi bi-sort-down me-2"></i>Sort By
                      </Form.Label>
                      <Form.Select 
                        value={sortOrder} 
                        onChange={(e) => setSortOrder(e.target.value)}
                        className={skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}
                      >
                        <option value="">Descending</option>
                        <option value="asc">Ascending</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={3} className="d-flex align-items-end">
                    <Button
                      variant="primary"
                      type="submit"
                      className="w-100"
                      disabled={loading || !selectedOutlet}
                    >
                      {loading ? (
                        <Spinner as="span" animation="border" size="sm" />
                      ) : (
                        "Generate Report"
                      )}
                    </Button>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="searchInput">
                      <Form.Label className="fw-semibold">
                        <i className="bi bi-search me-2"></i>Search
                      </Form.Label>
                      <InputGroup>
                        <FormControl
                          type="text"
                          placeholder="Search by name, mobile or email"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          className={skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}
                        />
                        <Button 
                          variant={skin === 'dark' ? 'secondary' : 'primary'} 
                          onClick={handleSubmit}
                          disabled={loading}
                        >
                          {loading ? (
                            <Spinner as="span" animation="border" size="sm" />
                          ) : (
                            <>
                              <Search className="me-1" />
                              Search
                            </>
                          )}
                        </Button>
                      </InputGroup>
                    </Form.Group>
                  </Col>

                 
                </Row>
              </Form>
            </div>

            {/* Results Section */}
            {filteredData?.length === 0 && !loading ? (
              <div className="d-flex justify-content-center align-items-center py-5 my-5 bg-light rounded-3">
                <div className="text-center">
                  <i className="bi bi-people fs-1 text-muted"></i>
                  <h5 className="text-muted mt-3">
                    {selectedOutlet ? "No customers found" : "Please select an outlet to view customer data"}
                  </h5>
                </div>
              </div>
            ) : (
              <>
                {/* Actions and Pagination - Top */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex">
                    <Button 
                      variant="outline-success"
                      className="me-2 fw-semibold"
                      onClick={handleDownloadExcel}
                      disabled={filteredData?.length === 0}
                    >
                      <FileEarmarkSpreadsheet className="me-2" />
                      { downloadBtnLoading ? <Spinner size='sm' /> : 'Download Excel Report' }
                    </Button>
                    {/* <Button 
                      variant="outline-warning"
                      onClick={() => generateReport(true)}
                      disabled={filteredData.length === 0}
                    >
                      <Printer className="me-2" />
                      Print with IDs
                    </Button> */}
                  </div>
                  
                  <div className="d-flex align-items-center">
                    <span className="me-2 text-muted">Show</span>
                    <Form.Select
                      style={{ width: 'auto' }}
                      value={recordsPerPage}
                      onChange={handleRecordsPerPageChange}
                      className={skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}
                    >
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                      <option value={300}>300</option>
                      <option value={500}>500</option>
                      <option value={2000}>2000</option>
                    </Form.Select>
                    <span className="ms-2 text-muted">entries</span>
                  </div>

                  <div className="d-flex align-items-center mt-3">
                    <Pagination>
                      <Pagination.Prev
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      />
                      {renderPaginationItems()}
                      <Pagination.Next
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                      />
                    </Pagination>
                  </div>
                </div>

                {/* Customers Table */}
                <div className="table-responsive border rounded-3">
                  <Table striped bordered hover className={`mb-0 ${skin === 'dark' ? 'dark-table' : ''}`}>
                    <thead className={skin === 'dark' ? 'bg-dark text-light' : 'bg-light'}>
                      <tr>
                        <th width="50">#</th>
                        <th width="120">Joined On</th>
                        <th>Customer Name</th>
                        <th>Contact</th>
                        <th>Outlet</th>
                        <th width="100" className="text-center">Orders</th>
                        <th width="100" className="text-center">Cancelled</th>
                        <th width="100" className="text-center">Visits</th>
                        <th width="120" className="text-end">Spendings (₹)</th>
                        <th width="100" className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="10" className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2 mb-0">Loading customers...</p>
                          </td>
                        </tr>
                      ) : filteredData.length > 0 ? (
                        filteredData.map((customer, index) => (
                          <tr key={index}>
                            <td className="text-muted">
                              {(currentPage - 1) * recordsPerPage + index + 1}
                            </td>
                            <td>
                              <small>{customer?.createdAt}</small>
                            </td>
                            <td>
                              <div className="fw-semibold">{customer?.name || '-'}</div>
                            </td>
                            <td>
                              <div className="text-primary">{customer?.mobile || '-'}</div>
                              <div className="text-muted small">{customer?.email || 'No email'}</div>
                            </td>
                            <td>
                              <small>{customer?.firstOrder?.outletName || '-'}</small>
                            </td>
                            <td className="text-center">
                              <Badge bg="primary" pill>
                                {customer?.orderCount || 0}
                              </Badge>
                            </td>
                            <td className="text-center">
                              <Badge bg="danger" pill>
                                {customer?.cancelledOrderCount || 0}
                              </Badge>
                            </td>
                            <td className="text-center">
                              <Badge bg="info" pill>
                                {customer?.numberOfVisits || 0}
                              </Badge>
                            </td>
                            <td className="text-end fw-semibold">
                              ₹ {customer?.totalGrandTotal?.toFixed(2) || '0.00'}
                            </td>
                            <td className="text-center">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => viewOrderDetails(customer)}
                              >
                                View
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="10" className="text-center py-4">
                            <i className="bi bi-exclamation-circle fs-4 text-muted"></i>
                            <p className="mt-2 mb-0 text-muted">
                              No customers found for the selected criteria
                            </p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>

                {/* Pagination - Bottom */}
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="text-muted">
                    Showing {(currentPage - 1) * recordsPerPage + 1} to{' '}
                    {Math.min(currentPage * recordsPerPage, filteredData.length)} of{' '}
                    {filteredData.length} entries
                  </div>
                  <Pagination>
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    />
                    {renderPaginationItems()}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              </>
            )}
          </Card.Body>
        </Card>

        {/* Customer Details Modal */}
        <Modal
          show={viewDetailsModal}
          onHide={closeModal}
          size="xl"
          centered
          className={skin === 'dark' ? 'dark-modal' : ''}
        >
          <Modal.Header closeButton className={skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}>
            <Modal.Title className="w-100 text-center">
              <i className="bi bi-person-lines-fill me-2"></i>Customer Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className={skin === 'dark' ? 'bg-dark text-light' : ''}>
            {selectedCustomer ? (
              <div className="p-3">
                <Row className="mb-4">
                  <Col md={4}>
                    <div className="d-flex align-items-center mb-3">
                      <div className="me-3">
                        <i className="bi bi-person-circle fs-1"></i>
                      </div>
                      <div>
                        <h5 className="mb-0">{selectedCustomer?.name}</h5>
                        <small className="text-muted">Customer ID: {selectedCustomer?._id}</small><br />
                        <small>
                          Joined On:{' '}
                          {(() => {
                            const rawDate = selectedCustomer?.createdAt; // e.g., "13-05-2025 17:20"
                            if (!rawDate) return '—';

                            const [datePart, timePart] = rawDate.split(' '); // "13-05-2025", "17:20"
                            const [day, month, year] = datePart.split('-');
                            const isoString = `${year}-${month}-${day}T${timePart}:00`; // "2025-05-13T17:20:00"

                            const parsedDate = new Date(isoString);
                            return isNaN(parsedDate.getTime())
                              ? 'Invalid Date'
                              : parsedDate.toLocaleString('en-GB', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: false,
                                }); // "13 May 2025, 17:20"
                          })()}
                        </small>

                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <h6 className="mb-1">Contact Information</h6>
                      <div className="text-primary">{selectedCustomer?.mobile}</div>
                      <div className="text-muted">{selectedCustomer?.email}</div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <h6 className="mb-1">Activity Summary</h6>
                      <div className="d-flex justify-content-between">
                        <div>
                          <div className="fw-bold">{selectedCustomer?.orderCount || 0}</div>
                          <small className="text-muted">Orders</small>
                        </div>
                        <div>
                          <div className="fw-bold">{selectedCustomer?.cancelledOrderCount || 0}</div>
                          <small className="text-muted">Cancelled</small>
                        </div>
                        <div>
                          <div className="fw-bold">{selectedCustomer?.numberOfVisits || 0}</div>
                          <small className="text-muted">Visits</small>
                        </div>
                        <div>
                          <div className="fw-bold">₹{selectedCustomer?.totalGrandTotal?.toFixed(2) || '0.00'}</div>
                          <small className="text-muted">Spent</small>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>

                <h5 className="mb-3">
                  <i className="bi bi-cart-check me-2"></i>Order History
                </h5>
                <div className="table-responsive">
                  <Table striped bordered hover responsive className={skin === 'dark' ? 'dark-table' : ''}>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Date</th>
                        <th>Order No</th>
                        <th>Outlet</th>
                        <th className="text-center">Status</th>
                        <th className="text-end">Amount (₹)</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderHistoryData?.length > 0 ? (
                        orderHistoryData?.map((order, index) => (
                          <tr key={index} onClick={() => handleOpenOrderDetailModal(order)} style={{ cursor: 'pointer' }}>
                            <td>{index + 1}</td>
                            <td>
                              {(() => {
                                const rawDate = order?.orderDetails?.createdAt; // e.g. "2025-10-07T10:05:13.527Z"
                                if (!rawDate) return '—';

                                const parsedDate = new Date(rawDate);

                                return isNaN(parsedDate.getTime())
                                  ? 'Invalid Date'
                                  : parsedDate.toLocaleString('en-GB', {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      hour12: false,
                                    });
                              })()}
                            </td>

                            <td>{order?.orderDetails?.order_sequence}{order?.orderDetails?.order_no}</td>
                            <td>{order?.outlet || '-'}</td>
                            <td className="text-center">
                              <Badge
                                pill
                                bg={
                                  order?.orderDetails?.orderStatus === "COMPLETED"
                                    ? "success"
                                    : order?.orderDetails?.orderStatus === "DELIVERED"
                                    ? "primary"
                                    : order?.orderDetails?.orderStatus === "CANCELLED"
                                    ? "danger"
                                    : "warning"
                                }
                              >
                                {order?.orderDetails?.orderStatus}
                              </Badge>
                            </td>
                            <td className="text-end">₹ {order?.orderDetails?.grandTotal?.toFixed(2) || '0.00'}</td>
                            <td className="text-center">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleOpenOrderDetailModal(order)}
                                >
                                  View
                                </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            <i className="bi bi-cart-x fs-4 text-muted"></i>
                            <p className="mt-2 mb-0 text-muted">No order history available</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                    {selectedCustomer?.totalGrandTotal !== 0 && (
                      <tfoot>
                        <tr>
                          <td colSpan="5" className="text-end fw-bold">Total:</td>
                          <td className="text-end fw-bold">
                            ₹ {selectedCustomer?.totalGrandTotal?.toFixed(2) || '0.00'}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </Table>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <i className="bi bi-exclamation-circle fs-4 text-muted"></i>
                <p className="mt-2 mb-0 text-muted">No customer details available</p>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className={skin === 'dark' ? 'bg-dark border-secondary' : ''}>
            <Button variant="secondary" onClick={closeModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Order Details Modal */}
        {openOrderDetailModal ? (
          <Modal
            show={openOrderDetailModal}
            onHide={() => setOpenOrderDetailModal(false)}
            size="lg"
            centered
          >
            <div className='shadow-lg' style={{ backgroundColor: '#f2f5f4' }}>
              <Modal.Header closeButton className="border-0">
                <Modal.Title className="w-100 text-center">
                  {selectedOrder?.order?.paymentMode === "SUBSCRIBED" ? (
                    <div className="d-flex justify-content-center align-items-center">
                      <span className="fs-4 me-2">🔄</span>
                      <span className="fw-bold text-primary">SUBSCRIPTION ORDER DETAILS</span>
                      <span className="fs-4 ms-2">📦</span>
                    </div>
                  ) : (
                    <div className="d-flex justify-content-center align-items-center">
                      <span className="fs-4 me-2">🛒</span>
                      <span className="fw-bold text-muted">ORDER DETAILS</span>
                      <span className="fs-4 ms-2">🧾</span>
                    </div>
                  )}
                </Modal.Title>
              </Modal.Header>
                <hr className='mb-0 mt-0' />
              <Modal.Body>
                {selectedOrder ? (
                  <div className="p-3">
                    <Row className="mb-2">
                      <Col xs={4}>
                        <h6>Order No:</h6>
                        <p>{selectedOrder?.order?.order_sequence}{selectedOrder?.order?.order_no}</p>
                      </Col>
                      <Col xs={4}>
                        <h6>Order Type :</h6>
                        <p>{selectedOrder?.order?.ordersType}</p>
                      </Col>
                      <Col xs={4}>
                        <h6>Date & Time:</h6>
                        <p>
                          {new Date(selectedOrder?.order?.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}{" / "}
                          {new Date(selectedOrder?.order?.createdAt).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: 'numeric',
                            hour12: true,
                          })}
                        </p>
                      </Col>
                    </Row>
                    <Row className="mb-2">
                      <Col xs={4}>
                        <h6>User Name :</h6>
                        <p>
                          {selectedOrder?.order?.outletUser
                            ? `${selectedOrder?.order?.outletUser?.name}`
                            : selectedOrder?.order?.kioskUser
                            ? `${selectedOrder?.order?.kioskUser?.name} [Kiosk User]`
                            : selectedOrder?.order?.user?.name}
                        </p>
                      </Col>
                      <Col xs={4}>
                        <h6>Mobile Number :</h6>
                        <p>
                          {selectedOrder?.order?.outletUser
                            ? selectedOrder?.order?.outletUser?.mobile
                            : selectedOrder?.order?.kioskUser
                            ? selectedOrder?.order?.kioskUser?.phone
                            : selectedOrder?.order?.user?.mobile}
                        </p>
                      </Col>
                      <Col xs={4}>
                        <h6>Source :</h6>
                        <p>
                          {selectedOrder?.order?.source}
                          {/* {selectedOrder?.order?.outletUser
                            ? "Complimentary Order"
                            : selectedOrder?.order?.kioskUser
                            ? "Kiosk"
                            : "APP"} */}
                        </p>
                      </Col>
                    </Row>
                    <Row className="mb-2">
                      <Col xs={4}>
                        <h6>Status :</h6>
                        <Badge 
                          bg={
                            selectedOrder?.order?.orderStatus === "COMPLETED"
                              ? "success"
                              : selectedOrder?.order?.orderStatus === "DELIVERED"
                              ? "primary"
                              : selectedOrder?.order?.orderStatus === "CONFIRMED"
                              ? "secondary"
                              : selectedOrder?.order?.orderStatus === "READY-TO-PICK"
                              ? "warning"
                              : "danger"
                          }
                          className="py-1 px-2"
                        >
                          {selectedOrder?.order?.orderStatus}
                        </Badge>
                      </Col>
                      <Col xs={4}>
                        <h6>Payment Gateway :</h6>
                        <p className="fw-semibold" style={{ color: selectedOrder?.order?.paymentGatewayProvider === "RAZORPAY" ? 'orangered' : 'blueviolet' }}>
                          {selectedOrder?.order?.paymentGatewayProvider} <br /> 
                          { selectedOrder?.order?.razorpayOrderId &&
                            <span className="text-muted pt-0" style={{ fontSize: '12px' }}>[{selectedOrder?.order?.razorpayOrderId}]</span>
                          }
                        </p>
                      </Col>
                      <Col xs={4}>
                        <h6>Payment Status :</h6>
                        <Badge
                          bg={
                            selectedOrder.order?.paymentStatus === "PAID"
                              ? "success"
                              : "danger"
                          }
                          className="py-1 px-2"
                        >
                          {selectedOrder?.order?.paymentStatus}
                        </Badge>
                      </Col>
                    </Row>
                    <Row className="mb-2">
                      <Col xs={4}>
                        <h6>Pickup Option :</h6>
                        <p>{selectedOrder?.order?.pickupOption}</p>
                      </Col>
                      <Col xs={4}>
                        <h6>Coupon Applied :</h6>
                        <p>{selectedOrder?.order?.coupon ? "YES" : "NO"}</p>
                      </Col>
                    </Row>
                    
                    {selectedOrder?.order?.coupon && (
                      <Row className="mb-2">
                        <Col xs={4} md={4}>
                          <h6>Coupon Name :</h6>
                          <p>{selectedOrder?.order?.coupon?.title}</p>
                        </Col>
                        <Col xs={4} md={4}>
                          <h6>Coupon Code :</h6>
                          <p className="text-danger">{selectedOrder?.order?.coupon?.couponCode}</p>
                        </Col>
                        <Col xs={4} md={4}>
                          <h6>Usage Type :</h6>
                          <p>{selectedOrder?.order?.coupon?.usageType}</p>
                        </Col>
                      </Row>
                    )}

                    {selectedOrder?.order?.comments && (
                      <Row className="mb-2">
                        <Col xs={12} md={12}>
                          <h6>Remark :</h6>
                          <p>{selectedOrder?.order?.comments}</p>
                        </Col>
                      </Row>
                    )}
                    
                    <Row className="mb-1">
                      <Col xs={12}>
                        <h6>Ordered Items :</h6>
                        <Table striped bordered hover responsive>
                          <thead>
                            <tr>
                              <th className="fw-semibold">Product</th>
                              <th className="text-center fw-semibold">Quantity</th>
                              <th className="text-center fw-semibold">Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedOrder?.orderItems?.map((item) => (
                              <tr key={item._id}>
                                <td>{item?.product?.name}
                                  {item?.addons?.length > 0 && (
                                    <ul style={{ fontSize: '14px' }}>
                                      {item.addons.map((addon) => (
                                        <li key={addon._id}>
                                          {addon.selectedValue} (₹{addon.price})
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </td>
                                <td className="text-center">{item?.quantity}</td>
                                <td className="text-end">
                                  ₹{selectedOrder?.order?.coupon?.isMrpDiscount && item.discount > 0 ? item.mrpRate.toFixed(2) : item.rate.toFixed(2)}
                                  {item?.addons?.length > 0 && (
                                    <ul style={{ fontSize: '14px', listStyleType: 'none' }}>
                                      {item?.addons?.map((addon) => (
                                        <li key={addon._id}>
                                          + (₹{addon?.price})
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </td>
                              </tr>
                            ))}
                            
                            {selectedOrder?.order?.platformFees !== 0 && (
                              <tr>
                                <td colSpan={2} className="text-end" style={{ fontSize: '14px' }}>
                                  Platform Fees:
                                </td>
                                <td style={{ fontSize: '14px' }} className="text-end">
                                  ₹ {Number(selectedOrder?.order?.platformFees).toFixed(2)}
                                </td>
                              </tr>
                            )}
                            
                            {selectedOrder?.order?.packingCharges !== 0 && (
                              <tr>
                                <td colSpan={2} className="text-end" style={{ fontSize: '14px' }}>Packing Fees:</td>
                                <td style={{ fontSize: '14px' }} className="text-end">
                                  ₹ { selectedOrder?.order?.packingCharges ? selectedOrder?.order?.packingCharges?.toFixed(2) : "0"}
                                </td>
                              </tr>
                            )}

                            {selectedOrder?.order?.deliveryCharges !== 0 && (
                              <tr>
                                <td colSpan={2} className="text-end" style={{ fontSize: '14px' }}>Delivery Charges:</td>
                                <td style={{ fontSize: '14px' }} className="text-end">
                                  ₹ {selectedOrder?.order?.deliveryCharges?.toFixed(2)}
                                </td>
                              </tr>
                            )}

                            <tr>
                              <td colSpan={2} className="text-end" style={{ fontSize: '14px' }}>Total :</td>
                              <td style={{ fontSize: '14px' }} className="text-end">
                                ₹ {calculateTotal()}
                              </td>
                            </tr>
                            
                            <tr style={{ borderBottom: '1px solid #9e9b9b' }}>
                              <td colSpan={2} className="text-end" style={{ fontSize: '14px' }}>Total Discount:</td>
                              <td style={{ fontSize: '14px' }} className="text-end">
                                (-) ₹ {selectedOrder?.order?.totalDiscount?.toFixed(2)}
                              </td>
                            </tr>

                            <tr>
                              <td colSpan={2} className="text-end" style={{ fontSize: '14px' }}></td>
                              <td style={{ fontSize: '14px' }} className="text-end text-muted fw-semibold">
                                ₹ { (calculateTotal() - (selectedOrder?.order?.totalDiscount || 0)).toFixed(2) }
                              </td>
                            </tr>

                            <tr style={{ borderBottom: '1px solid #000' }}>
                              <td colSpan={2} className="text-end" style={{ fontSize: '14px' }}>Total Tax Amount:</td>
                              <td style={{ fontSize: '14px' }} className="text-end">
                                (+) ₹ {selectedOrder?.order?.totalTaxAmount?.toFixed(2)}
                              </td>
                            </tr>

                            <tr style={{ borderBottom: '1px solid #000' }}>
                              <td colSpan={2} className="text-end"><strong>Grand Total:</strong></td>
                              <td className="text-end fw-semibold">
                                ₹ {selectedOrder?.orderItems?.reduce((total, item) => total + item.grandTotal, 0).toFixed(2)}
                              </td>
                            </tr>
                          </tbody>
                        </Table>
                      </Col>
                    </Row>

                    <Row className="mb-1">
                      <Col xs={12}>
                        <h6>Order Status Log :</h6>
                        <Table striped bordered hover responsive>
                          <thead>
                            <tr>
                              <th className="fw-semibold">Order Status</th>
                              <th className="fw-semibold">Time</th>
                              <th className="fw-semibold">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedOrder?.orderStatusLogs?.length > 0 ? (
                              selectedOrder.orderStatusLogs.map((log) => (
                                <tr key={log._id}>
                                  <td>
                                    <Badge
                                      bg={
                                        log.orderStatus === "COMPLETED"
                                          ? "success"
                                          : log.orderStatus === "DELIVERED"
                                          ? "primary"
                                          : log.orderStatus === "CONFIRMED"
                                          ? "secondary"
                                          : log.orderStatus === "READY-TO-PICK"
                                          ? "warning"
                                          : log.orderStatus === "CANCELLED"
                                          ? "danger"
                                          : "dark"
                                      }
                                      className="py-1 px-2"
                                    >
                                      {log.orderStatus}
                                    </Badge>
                                  </td>
                                  <td>
                                    {new Date(log.createdAt).toLocaleTimeString('en-US', {
                                      hour: 'numeric',
                                      minute: 'numeric',
                                      hour12: true,
                                    })}
                                  </td>
                                  <td>
                                    {new Date(log.createdAt).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric',
                                    })}                                
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={3} className="text-center">
                                  No order status logs available.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </Col>
                    </Row>
                  </div>
                ) : (
                  <p>No order details available.</p>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setOpenOrderDetailModal(false)}>
                  Close
                </Button>
              </Modal.Footer>
            </div>
          </Modal>
        ) : null}
        

      </div>
    </React.Fragment>
  );
};

export default CustomerReports;